<!-- Landmark & skip target for keyboard users -->
<main id="ecitation-main" class="container" tabindex="-1">
  <div class="topheadtitle">
    <!-- Preserve heading level as provided, add aria-live for title changes -->
    <h4 aria-live="polite">{{showReportResults ? 'Citation Status' : 'Track Citation'}}</h4>
  </div>

  <!-- SEARCH FORM -->
  @if (!showReportResults) {
    <div class="form-box">
      <div class="formPage">
        <!-- Submit through the form so both click and Enter trigger one request. -->
        <form [formGroup]="searchForm" (ngSubmit)="onSearch()" aria-describedby="search-help">
          <p id="search-help" class="visually-hidden">
            Enter a citation number or refine by issuer, status, and date range, then select Search.
          </p>
          <!-- Citation Number -->
          <label class="fw-600" for="citationNumber" style="margin-right: 13px;">Citation number</label>
          <input formControlName="citationNumber" id="citationNumber" type="text"
            class="Input-width Input-width-1" placement="top" triggers="mouseenter:mouseleave focusin:focusout"
            ngbPopover="A minimum of 1 initial character entry is needed for a specific citation search"
            aria-describedby="citationNumber-hint" inputmode="latin" autocomplete="off" />
            <span id="citationNumber-hint" class="text-para" style="margin: 0 28px 0 10px;">
              Alphanumeric characters only — please do not include the information <i>after</i> the dash
            </span>
            <hr />
            <!-- Issued By & Status -->
            <!-- Group related controls for screen readers -->
            <div class="destination" aria-labelledby="issued-status-legend">
              <span id="issued-status-legend" class="visually-hidden">Filter by Issued By and Status</span>
              <label class="fw-600" for="issuedBy" style="margin-right: 65px;">Issued By</label>
              <select id="issuedBy" formControlName="issuedBy" class="option-d">
                <option value="ALL">All</option>
                <option value="Not Specified">Not Specified</option>
                @for (item of agenciesList; track item) {
                  <option [value]="item.agencyName">
                    {{ item.agencyName }}
                  </option>
                }
              </select>
              <label class="fw-600" for="status" style="margin:0px 35px 0px 100px;">Status</label>
              <select id="status" formControlName="status" multiple aria-describedby="status-hint">
                <option value="ALL">All</option>
                <option value="Delivered">Delivered</option>
                <option value="Rejected">Rejected</option>
                <option value="Failed">Failed</option>
                <option value="Queued">Queued</option>
              </select>
              <span id="status-hint" class="visually-hidden">Use Ctrl or Command to select multiple
              statuses.</span>
            </div>
            <hr />
            <!-- Date Range -->
            <div class="box-date" aria-labelledby="txdate-legend" aria-describedby="txdate-hint">
              <span id="txdate-legend" class="fw-600" style="margin-right: 3px;">Transmission Date</span>
              <input type="date" formControlName="transmissionStartDate" class="Input-width" autocomplete="off"
                aria-label="Transmission Start Date" />
                <span aria-hidden="true"> to </span>
                <input type="date" formControlName="transmissionEndDate" class="Input-width" autocomplete="off"
                  aria-label="Transmission End Date" />
                  <span id="txdate-hint" class="text-para">Enter just one date if a single date is
                  desired</span><br />
                </div>
                <hr />
                <!-- Actions -->
                <div class="formpage-btn">
                  <button type="submit" class="btn-Submit">Search</button>&nbsp;&nbsp;
                  <!-- Prevent unintended submit -->
                  <button type="button" class="btn-Submit" (click)="resetForm()">Reset</button>
                </div>
              </form>
            </div>
          </div>
        }

        <!-- RESULTS -->
        @if (showReportResults) {
          <div>
            <!-- Table view (multiple) -->
            @if (outPut.totalElements > 1) {
              <div class="table-container mt-2">
                <table aria-describedby="results-summary">
                  <caption>Public eCitation Status Search Results</caption>
                  <thead>
                    <tr>
                      <th scope="col"
                        [attr.aria-sort]="sortKey === 'citationNumber' ? (sortAsc ? 'ascending' : 'descending') : 'none'">
                        <button type="button" class="btn-link sort-header" title="Sort Citation Number"
                          (click)="onSort($event,'citationNumber',true)">
                          Citation Number <i class="fa fa-sort" aria-hidden="true"></i>
                        </button>
                      </th>
                      <th scope="col"
                        [attr.aria-sort]="sortKey === 'destination' ? (sortAsc ? 'ascending' : 'descending') : 'none'">
                        <button type="button" class="btn-link sort-header" title="Sort Destination"
                          (click)="onSort($event,'destination',true)">
                          Destination <i class="fa fa-sort" aria-hidden="true"></i>
                        </button>
                      </th>
                      <th scope="col"
                        [attr.aria-sort]="sortKey === 'agencyName' ? (sortAsc ? 'ascending' : 'descending') : 'none'">
                        <button type="button" class="btn-link sort-header" title="Sort Issued By"
                          (click)="onSort($event,'agencyName',true)">
                          Issued By <i class="fa fa-sort" aria-hidden="true"></i>
                        </button>
                      </th>
                      <th scope="col"
                        [attr.aria-sort]="sortKey === 'dispositionCode' ? (sortAsc ? 'ascending' : 'descending') : 'none'">
                        <button type="button" class="btn-link sort-header" title="Sort Disposition"
                          (click)="onSort($event,'dispositionCode',true)">
                          Disposition <i class="fa fa-sort" aria-hidden="true"></i>
                        </button>
                      </th>
                      <th scope="col"
                        [attr.aria-sort]="sortKey === 'transmissionDate' ? (sortAsc ? 'ascending' : 'descending') : 'none'">
                        <button type="button" class="btn-link sort-header" title="Sort Transmission Date"
                          (click)="onSort($event,'transmissionDate',true)">
                          Transmitted <i class="fa fa-sort" aria-hidden="true"></i>
                        </button>
                      </th>
                      <th scope="col">Court Date</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of getItems(); track item; let i = $index) {
                      <tr>
                        <td>{{ item.citationNumber }}</td>
                        <td>
                          @switch (item.destination) {
                            @case ('DOT-EARS') {
                              <span>EARS</span>
                            }
                            @case ('CCAP-DOT') {
                              <span>CCAP</span>
                            }
                            @case ('CCAP-DNR') {
                              <span>CCAP</span>
                            }
                            @case ('DOT-COWS') {
                              <span>COWS</span>
                            }
                            @case ('MUNI') {
                              <span>MUNI</span>
                            }
                            @case ('DAIT-PROTECT') {
                              <span>DAIT-PROTECT</span>
                            }
                            @case ('DNR-CTS') {
                              <span>CTS</span>
                            }
                            @case ('NOWHERE') {
                              <span>LOCAL</span>
                            }
                            @default {
                              <span>-</span>
                            }
                          }
                        </td>
                        <td>{{ item.agencyName }}</td>
                        <td>{{ item.dispositionCode }}</td>
                        <td>{{ item.transmissionDate }}</td>
                        <td>{{ item.courtDate }}</td>
                        <td>{{ details[item.status] }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
                <!-- Live announcement of the current query & counts -->
                <div id="results-summary" class="text-center" aria-live="polite">
                  {{ searchQuery() }}
                </div>
                <div class="d-flex justify-content-between align-items-center">
                  <div aria-live="polite">
                    <span>
                      {{ outPut.totalElements }} items found, displaying {{ getCurrentPageTotal() }} of {{
                      outPut.totalElements }}
                    </span>
                  </div>
                  <!-- Add an accessible label to pagination -->
                  <ngb-pagination [collectionSize]="outPut.totalElements" [(page)]="currentPage" [ellipses]="true"
                    [maxSize]="10" (pageChange)="onPageChange(currentPage)" [pageSize]="pageSize" [boundaryLinks]="true"
                  aria-label="Pagination for search results"></ngb-pagination>
                  <button type="button" class="btn btn-outline-primary" (click)="showReportResults = false; resetForm()">
                    Go back to previous page
                  </button>
                </div>
                <br />
              </div>
            }
            <!-- Card view (single/none) -->
            @if ((outPut.totalElements == 1 || outPut.totalElements == 0)) {
              <div class="container">
                @for (item of outPut.content; track item) {
                  <div class="py-3">
                    <div class="compAccentBarBot" style="height:1.2em"></div>
                    <!-- Fix table structure: use caption + thead for headers (none here) and move rows to tbody -->
                    <table class="searchInputBox2 accessible-caption" aria-label="Public eCitation Status Search Results">
                      <caption>Public eCitation Status Search Results</caption>
                      <tbody>
                        <tr>
                          <th class="formFieldLabel" scope="row">Citation Number:</th>
                          <td class="formField">{{ item.citationNumber }}</td>
                          <th class="formFieldLabel" scope="row">Destination:</th>
                          <td class="formField">{{ item.destination }}</td>
                        </tr>
                        <tr>
                          <th class="formFieldLabel" scope="row">Issued By:</th>
                          <td class="formField">{{ item.agencyName }}</td>
                          <th class="formFieldLabel" scope="row">Disposition:</th>
                          <td class="formField">{{ item.dispositionCode }}</td>
                        </tr>
                        <tr>
                          <th class="formFieldLabel" scope="row">Transmission Date:</th>
                          <td class="formField">{{ item.transmissionDate }}</td>
                          <th class="formFieldLabel" scope="row">Court Date:</th>
                          <td class="formField">{{ item.courtDate }}</td>
                        </tr>
                        <tr>
                          <th class="formFieldLabel" scope="row">Violation County:</th>
                          <td class="formField">{{ item.countyName }}</td>
                          <th class="formFieldLabel" scope="row">Status:</th>
                          <td class="formField">{{ details[item.status] }}</td>
                        </tr>
                        <tr>
                          <th class="formFieldLabel" scope="row">Violation Date:</th>
                          <td class="formField">{{ item.violationDate }}</td>
                          <th class="formFieldLabel" scope="row">Served Date:</th>
                          <td class="formField">{{ item.servedDate }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div class="compAccentBarBot" style="height:1.2em"></div>
                  </div>
                }
                @if (outPut.content.length == 0) {
                  <div class="py-3 text-center" aria-live="polite">
                    No records found
                  </div>
                }
                <div class="text-center" aria-live="polite">
                  {{ searchQuery() }}
                </div>
                <button style="float: right;" type="button" class="btn btn-outline-primary"
                  (click)="showReportResults = false; resetForm()">
                  Go back to previous page
                </button>
              </div>
            }
            <div class="footer-heading text-center">
              <h6>For Public Information</h6>
            </div>
          </div>
        }

        <ng-template #backBtn> </ng-template>
      </main>
